import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import clinicaService from '../../services/clinicaService';
import api from '../../services/api';
import {
  Box, Typography, TextField, Button, Paper, Divider,
  ToggleButton, ToggleButtonGroup, Chip, IconButton, Autocomplete,
} from '@mui/material';
import CID_ODONTOLOGICO from '../../data/cidOdontologico';
import PrintIcon from '@mui/icons-material/Print';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DescriptionIcon from '@mui/icons-material/Description';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MedicationIcon from '@mui/icons-material/Medication';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ORANGE = '#F97316';

// ─── Receita ────────────────────────────────────────────────────────────────

const ReceitaForm = ({ form, setForm, fieldSx }) => {
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  const addMed = () => setForm(p => ({ ...p, medicamentos: [...p.medicamentos, { nome: '', posologia: '' }] }));
  const setMed = (i, f, v) => setForm(p => {
    const m = [...p.medicamentos]; m[i] = { ...m[i], [f]: v }; return { ...p, medicamentos: m };
  });
  const removeMed = (i) => setForm(p => ({ ...p, medicamentos: p.medicamentos.filter((_, j) => j !== i) }));

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
          Paciente
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField label="Nome completo" value={form.nome} onChange={set('nome')} size="small" fullWidth sx={fieldSx} />
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
            <TextField label="Data" type="date" value={form.data} onChange={set('data')} size="small" fullWidth InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <Autocomplete
              freeSolo
              size="small"
              options={CID_ODONTOLOGICO}
              getOptionLabel={(option) => typeof option === 'string' ? option : `${option.codigo} — ${option.descricao}`}
              isOptionEqualToValue={(option, value) => option.codigo === value?.codigo}
              filterOptions={(options, { inputValue }) => {
                const termo = inputValue.trim().toLowerCase();
                if (!termo) return options.slice(0, 50);
                return options.filter(o =>
                  o.codigo.toLowerCase().includes(termo) || o.descricao.toLowerCase().includes(termo)
                ).slice(0, 50);
              }}
              inputValue={form.cid}
              onInputChange={(e, newValue) => setForm(p => ({ ...p, cid: newValue }))}
              onChange={(e, newValue) => {
                const texto = newValue ? (typeof newValue === 'string' ? newValue : newValue.codigo) : '';
                setForm(p => ({ ...p, cid: texto }));
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.codigo}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{option.codigo}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.descricao}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="CID" placeholder="Buscar código ou diagnóstico" fullWidth sx={fieldSx} />
              )}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Medicamentos
          </Typography>
          <Button size="small" startIcon={<AddIcon />} onClick={addMed} sx={{ color: ORANGE, fontWeight: 700, fontSize: '0.75rem', minWidth: 0 }}>
            Adicionar
          </Button>
        </Box>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {form.medicamentos.map((med, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1.5, borderRadius: 2, borderColor: 'rgba(249,115,22,0.2)', position: 'relative' }}>
              <IconButton size="small" onClick={() => removeMed(i)} sx={{ position: 'absolute', top: 4, right: 4, color: '#ef4444' }}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
              <Box display="flex" flexDirection="column" gap={1}>
                <TextField label={`Medicamento ${i + 1}`} value={med.nome} onChange={e => setMed(i, 'nome', e.target.value)} size="small" fullWidth sx={fieldSx} />
                <TextField label="Posologia / Instruções" value={med.posologia} onChange={e => setMed(i, 'posologia', e.target.value)} size="small" fullWidth multiline rows={2} sx={fieldSx} placeholder="Ex: 1 comprimido de 8 em 8 horas por 7 dias" />
              </Box>
            </Paper>
          ))}
          {form.medicamentos.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 2, color: 'text.disabled', fontSize: '0.85rem' }}>
              Nenhum medicamento adicionado ainda.
            </Box>
          )}
        </Box>
      </Box>
      <TextField label="Observações" value={form.observacoes} onChange={set('observacoes')} size="small" fullWidth multiline rows={2} sx={fieldSx} />
    </Box>
  );
};

const ReceitaPreview = ({ form, user, logo, assinatura, clinica }) => {
  const dataFormatada = form.data ? form.data.split('-').reverse().join('/') : '';
  return (
    <Box
      className="atestado-preview-card"
      sx={{ width: '14cm', minHeight: '18cm', background: '#fff', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.13)', borderRadius: '4px', fontFamily: '"Inter", Arial, sans-serif' }}
    >
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: '65%', height: '56px', background: `linear-gradient(135deg, transparent 28%, ${ORANGE} 28%, ${ORANGE} 55%, #1a1a2e 55%)` }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: '18px', pt: '14px', pb: 0, position: 'relative', zIndex: 2, minHeight: '70px' }}>
        <Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#1a1a2e', letterSpacing: '0.5px' }}>
            DR(A). {user?.nome || 'Nome do Dentista'}
          </Typography>
          <Typography sx={{ fontSize: '9px', color: '#64748b', mt: '2px' }}>{user?.cro ? `CRO ${user.cro}` : ''}</Typography>
        </Box>
        {logo && <img src={logo} alt="Logo" style={{ maxHeight: 44, maxWidth: 100, objectFit: 'contain' }} />}
      </Box>

      <Box sx={{ px: '22px', pt: '8px', flex: 1 }}>
        <Typography sx={{ textAlign: 'center', fontSize: '14px', fontWeight: 800, letterSpacing: '1.5px', color: '#1a1a2e', my: '12px', textTransform: 'uppercase' }}>
          RECEITUÁRIO ODONTOLÓGICO
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: form.cid ? '4px' : '10px', fontSize: '9px', color: '#475569' }}>
          <span><b style={{ color: '#1a1a2e' }}>Paciente:</b> {form.nome || '_______________'}</span>
          <span><b style={{ color: '#1a1a2e' }}>Data:</b> {dataFormatada}</span>
        </Box>
        {form.cid && (
          <Box sx={{ mb: '10px', fontSize: '9px', color: '#475569' }}>
            <b style={{ color: '#1a1a2e' }}>CID:</b> {form.cid}
          </Box>
        )}

        <Box sx={{ borderTop: '1px solid #e2e8f0', pt: '10px' }}>
          {form.medicamentos.length === 0 ? (
            <Typography sx={{ fontSize: '9px', color: '#94a3b8', fontStyle: 'italic' }}>Nenhum medicamento prescrito.</Typography>
          ) : (
            form.medicamentos.map((med, i) => (
              <Box key={i} sx={{ mb: '10px' }}>
                <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#1a1a2e' }}>
                  {i + 1}. {med.nome || `Medicamento ${i + 1}`}
                </Typography>
                {med.posologia && (
                  <Typography sx={{ fontSize: '9px', color: '#475569', ml: '12px', lineHeight: 1.6 }}>
                    {med.posologia}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>

        {form.observacoes && (
          <Box sx={{ borderTop: '1px solid #e2e8f0', pt: '8px', mt: '8px' }}>
            <Typography sx={{ fontSize: '9px', color: '#475569' }}>
              <b style={{ color: '#1a1a2e' }}>Observações:</b> {form.observacoes}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ position: 'relative', px: '22px', pb: '18px', mt: '10px' }}>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '75%', height: '60px', background: `linear-gradient(135deg, #1a1a2e 42%, ${ORANGE} 42%, ${ORANGE} 62%, transparent 62%)`, zIndex: 1 }} />
        <Box sx={{ textAlign: 'center', width: '55%', ml: 'auto', position: 'relative', zIndex: 2 }}>
          {assinatura
            ? <img src={assinatura} alt="Assinatura" style={{ maxHeight: 40, maxWidth: 180, objectFit: 'contain', marginBottom: 2 }} />
            : <Box sx={{ borderTop: '1px solid #94a3b8', mb: '3px' }} />
          }
          <Typography sx={{ fontSize: '8px', fontWeight: 700, color: '#475569' }}>Assinatura do Dentista</Typography>
        </Box>
        <Box sx={{ fontSize: '7px', color: '#94a3b8', mt: '8px', textAlign: 'right', position: 'relative', zIndex: 2 }}>
          {clinica?.telefone && <>{clinica.telefone} | </>}
          {clinica?.endereco ? <>{clinica.endereco}{clinica.numero ? `, nº ${clinica.numero}` : ''}<br /></> : null}
          {clinica?.email || ''}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Atestado / Declaração ───────────────────────────────────────────────────

const AtestadoForm = ({ form, setForm, fieldSx }) => {
  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
          Tipo de Documento
        </Typography>
        <ToggleButtonGroup
          value={form.tipoDoc}
          exclusive
          onChange={(_, v) => { if (v) setForm(p => ({ ...p, tipoDoc: v })); }}
          size="small"
          fullWidth
        >
          <ToggleButton value="atestado" sx={{ flex: 1, '&.Mui-selected': { bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, borderColor: ORANGE }, fontWeight: 600 }}>
            Atestado
          </ToggleButton>
          <ToggleButton value="declaracao" sx={{ flex: 1, gap: 0.5, '&.Mui-selected': { bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, borderColor: ORANGE }, fontWeight: 600 }}>
            <AccessTimeIcon sx={{ fontSize: 15 }} /> Declaração de Horas
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
          Dados do Paciente
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField label="Nome completo" value={form.nome} onChange={set('nome')} size="small" fullWidth sx={fieldSx} />
          <TextField label="CPF" value={form.cpf} onChange={set('cpf')} size="small" fullWidth sx={fieldSx} />
          <TextField label="Endereço" value={form.endereco} onChange={set('endereco')} size="small" fullWidth sx={fieldSx} />
          <TextField label="Cidade" value={form.cidade} onChange={set('cidade')} size="small" fullWidth sx={fieldSx} />
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" sx={{ fontWeight: 700, color: ORANGE, textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, display: 'block' }}>
          Atendimento
        </Typography>
        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField label="Data" type="date" value={form.data} onChange={set('data')} size="small" fullWidth InputLabelProps={{ shrink: true }} sx={fieldSx} />
          {form.tipoDoc === 'declaracao' && (
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.5}>
              <TextField label="Hora início" value={form.horaInicio} onChange={set('horaInicio')} placeholder="08:00" size="small" sx={fieldSx} />
              <TextField label="Hora fim" value={form.horaFim} onChange={set('horaFim')} placeholder="09:00" size="small" sx={fieldSx} />
            </Box>
          )}
          {form.tipoDoc === 'atestado' && (
            <TextField label="Dias de afastamento" value={form.dias} onChange={set('dias')} size="small" fullWidth sx={fieldSx} />
          )}
          <TextField label="Procedimento realizado" value={form.procedimento} onChange={set('procedimento')} size="small" fullWidth sx={fieldSx} />
        </Box>
      </Box>
    </Box>
  );
};

const AtestadoPreview = ({ form, user, logo, assinatura, clinica }) => {
  const dataFormatada = form.data ? form.data.split('-').reverse().join('/') : '';
  return (
    <Box
      className="atestado-preview-card"
      sx={{ width: '14cm', minHeight: '18cm', background: '#fff', position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.13)', borderRadius: '4px', fontFamily: '"Inter", Arial, sans-serif' }}
    >
      <Box sx={{ position: 'absolute', top: 0, right: 0, width: '65%', height: '56px', background: `linear-gradient(135deg, transparent 28%, ${ORANGE} 28%, ${ORANGE} 55%, #1a1a2e 55%)` }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', px: '18px', pt: '14px', pb: 0, position: 'relative', zIndex: 2, minHeight: '70px' }}>
        <Box>
          <Typography sx={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: '#1a1a2e', letterSpacing: '0.5px' }}>
            DR(A). {user?.nome || 'Nome do Dentista'}
          </Typography>
          <Typography sx={{ fontSize: '9px', color: '#64748b', mt: '2px' }}>{user?.cro ? `CRO ${user.cro}` : ''}</Typography>
        </Box>
        {logo && <img src={logo} alt="Logo" style={{ maxHeight: 44, maxWidth: 100, objectFit: 'contain' }} />}
      </Box>

      <Box sx={{ px: '22px', pt: '8px', flex: 1 }}>
        <Typography sx={{ textAlign: 'center', fontSize: '14px', fontWeight: 800, letterSpacing: '1.5px', color: '#1a1a2e', my: '12px', textTransform: 'uppercase' }}>
          {form.tipoDoc === 'declaracao' ? 'DECLARAÇÃO DE HORAS' : 'ATESTADO ODONTOLÓGICO'}
        </Typography>
        <Typography component="div" sx={{ fontSize: '10px', lineHeight: 2, textAlign: 'justify', color: '#1e293b' }}>
          {form.tipoDoc === 'declaracao' ? (
            <>Declaramos para devidos fins que <b>{form.nome || '___________'}</b><br />
            CPF: <b>{form.cpf || '___________'}</b><br />
            residente e domiciliado(a) à <b>{form.endereco || '___________'}</b><br />
            esteve sob tratamento odontológico neste consultório, no período das <b>{form.horaInicio || '__:__'}</b> às <b>{form.horaFim || '__:__'}</b> horas do dia <b>{dataFormatada}</b>.<br />
            <b>Procedimento:</b> {form.procedimento || '___________'}</>
          ) : (
            <>Atesto para devidos fins que <b>{form.nome || '___________'}</b><br />
            CPF: <b>{form.cpf || '___________'}</b><br />
            residente e domiciliado(a) à <b>{form.endereco || '___________'}</b><br />
            compareceu a esta clínica odontológica no dia <b>{dataFormatada}</b>{form.dias ? <>, necessitando de <b>{form.dias}</b> dias de afastamento</> : ''}.<br />
            <b>Procedimento:</b> {form.procedimento || '___________'}</>
          )}
        </Typography>
        <Box sx={{ textAlign: 'right', fontSize: '9px', color: '#475569', mt: '14px' }}>
          <b style={{ fontSize: 10, color: '#1a1a2e' }}>{clinica?.cidade || form.cidade || 'Cidade'}</b>, <b style={{ fontSize: 10, color: '#1a1a2e' }}>{dataFormatada}</b>
        </Box>
      </Box>

      <Box sx={{ position: 'relative', px: '22px', pb: '18px', mt: '10px' }}>
        <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: '75%', height: '60px', background: `linear-gradient(135deg, #1a1a2e 42%, ${ORANGE} 42%, ${ORANGE} 62%, transparent 62%)`, zIndex: 1 }} />
        <Box sx={{ textAlign: 'center', width: '55%', ml: 'auto', position: 'relative', zIndex: 2 }}>
          {assinatura
            ? <img src={assinatura} alt="Assinatura" style={{ maxHeight: 40, maxWidth: 180, objectFit: 'contain', marginBottom: 2 }} />
            : <Box sx={{ borderTop: '1px solid #94a3b8', mb: '3px' }} />
          }
          <Typography sx={{ fontSize: '8px', fontWeight: 700, color: '#475569' }}>Assinatura do Dentista</Typography>
        </Box>
        <Box sx={{ fontSize: '7px', color: '#94a3b8', mt: '8px', textAlign: 'right', position: 'relative', zIndex: 2 }}>
          {clinica?.telefone && <>{clinica.telefone} | </>}
          {clinica?.endereco ? <>{clinica.endereco}{clinica.numero ? `, nº ${clinica.numero}` : ''}<br /></> : null}
          {clinica?.email || ''}
        </Box>
      </Box>
    </Box>
  );
};

// ─── Componente principal ────────────────────────────────────────────────────

const EmitirDocumento = ({ paciente, tipo = 'atestado' }) => {
  const cpfPaciente = paciente?.cpf || paciente?.CPF || paciente?.cpf_cnpj || paciente?.cpfCnpj || '';
  const enderecoCompleto = paciente ? [
    paciente.logradouro, paciente.numero, paciente.complemento, paciente.bairro
  ].filter(Boolean).join(', ') : '';

  const isReceita = tipo === 'receita';

  const [atestadoForm, setAtestadoForm] = useState({
    nome: paciente?.nome || '',
    cpf: cpfPaciente,
    endereco: enderecoCompleto || paciente?.endereco || '',
    cidade: paciente?.cidade || '',
    data: new Date().toISOString().slice(0, 10),
    horaInicio: '', horaFim: '', dias: '', procedimento: '',
    tipoDoc: tipo === 'declaracao' ? 'declaracao' : 'atestado',
  });

  const [receitaForm, setReceitaForm] = useState({
    nome: paciente?.nome || '',
    data: new Date().toISOString().slice(0, 10),
    cid: '',
    medicamentos: [{ nome: '', posologia: '' }],
    observacoes: '',
  });

  const { user } = useAuth();
  const [logo, setLogo] = useState('');
  const [assinatura, setAssinatura] = useState('');
  const [clinica, setClinica] = useState(null);

  useEffect(() => {
    if (!user?.clinicaId) return;
    clinicaService.buscarClinica(user.clinicaId).then(d => {
      setClinica(d);
      if (d.logo) setLogo(d.logo);
      if (d.assinatura) setAssinatura(d.assinatura);
    }).catch(() => {});
  }, [user?.clinicaId]);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
      '&.Mui-focused fieldset': { borderColor: ORANGE },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE },
  };

  const handlePrint = () => {
    const el = document.querySelector('.atestado-preview-card');
    if (!el) return;
    const printContents = el.outerHTML;

    // Copia todos os <style> e <link rel="stylesheet"> que o MUI injeta na página
    const styleElements = Array.from(document.querySelectorAll('style')).map(s => s.outerHTML).join('');
    const linkElements = Array.from(document.querySelectorAll('link[rel="stylesheet"]')).map(l => l.outerHTML).join('');

    const printWindow = window.open('', '', 'height=800,width=700');
    printWindow.document.write(`
      <html>
        <head>
          <title>Imprimir</title>
          ${linkElements}
          ${styleElements}
          <style>
            * { box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            body { background: #fff; display: flex; justify-content: center; padding: 20px; font-family: 'Inter', Arial, sans-serif; }
            @media print { body { padding: 0; margin: 0; } }
          </style>
        </head>
        <body>${printContents}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 800);
  };

  const [enviandoWhatsApp, setEnviandoWhatsApp] = useState(false);
  const [erroWhatsApp, setErroWhatsApp] = useState('');

  const handleWhatsApp = async () => {
    setErroWhatsApp('');
    setEnviandoWhatsApp(true);
    try {
      const form = isReceita ? receitaForm : atestadoForm;
      const payload = { tipo: isReceita ? 'receita' : atestadoForm.tipoDoc, ...form };
      const blob = await api.post('/documentos/pdf', payload, { responseType: 'blob' });

      const nomeArquivo = `${payload.tipo}-${(form.nome || 'paciente').replace(/\s+/g, '-')}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeArquivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const numero = (paciente?.whatsapp || paciente?.telefone || '').replace(/\D/g, '');
      const mensagem = `Olá ${form.nome || ''}, segue ${isReceita ? 'sua receita' : 'seu atestado'}. Acabei de baixar o PDF — é só anexar aqui no WhatsApp (clipe 📎).`;
      window.open(`https://wa.me/${numero ? '55' + numero : ''}?text=${encodeURIComponent(mensagem)}`, '_blank');
    } catch (err) {
      setErroWhatsApp('Erro ao gerar o PDF. Tente novamente.');
    } finally {
      setEnviandoWhatsApp(false);
    }
  };

  const [documentoAssinatura, setDocumentoAssinatura] = useState(null); // { id, link, status, downloadUrl }
  const [assinandoDoc, setAssinandoDoc] = useState(false);
  const [verificandoAssinatura, setVerificandoAssinatura] = useState(false);
  const [erroAssinatura, setErroAssinatura] = useState('');

  const handleAssinarDigital = async () => {
    setErroAssinatura('');
    setAssinandoDoc(true);
    try {
      const form = isReceita ? receitaForm : atestadoForm;
      const payload = { tipo: isReceita ? 'receita' : atestadoForm.tipoDoc, pacienteId: paciente?.id || null, ...form };
      const resp = await api.post('/documentos/assinar', payload);
      setDocumentoAssinatura({ id: resp.documentoId, link: resp.link, status: 'pendente' });
      window.open(resp.link, '_blank');
    } catch (err) {
      setErroAssinatura(err?.response?.data?.message || 'Erro ao enviar documento pra assinatura digital.');
    } finally {
      setAssinandoDoc(false);
    }
  };

  const handleVerificarAssinatura = async () => {
    if (!documentoAssinatura?.id) return;
    setErroAssinatura('');
    setVerificandoAssinatura(true);
    try {
      const resp = await api.get(`/documentos/${documentoAssinatura.id}/sincronizar`);
      setDocumentoAssinatura(prev => ({ ...prev, status: resp.status, downloadUrl: resp.downloadUrl }));
    } catch (err) {
      setErroAssinatura('Erro ao verificar assinatura.');
    } finally {
      setVerificandoAssinatura(false);
    }
  };

  const tituloEditor = isReceita ? 'Receita Odontológica' : (atestadoForm.tipoDoc === 'declaracao' ? 'Declaração de Horas' : 'Atestado Odontológico');

  return (
    <Box sx={{ display: 'flex', gap: 3, p: { xs: 2, md: 3 }, minHeight: '80vh', background: '#f8fafc', flexWrap: 'wrap' }}>

      {/* Painel esquerdo — formulário */}
      <Paper sx={{ flex: '1 1 340px', minWidth: 300, borderRadius: 3, p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, border: '1px solid rgba(249,115,22,0.15)', borderTop: '4px solid #F97316', boxShadow: '0 2px 12px rgba(249,115,22,0.06)' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {isReceita ? <MedicationIcon sx={{ color: ORANGE, fontSize: 26 }} /> : <DescriptionIcon sx={{ color: ORANGE, fontSize: 26 }} />}
          <Box>
            <Typography fontWeight={800} fontSize="1.1rem">{tituloEditor}</Typography>
            <Typography variant="caption" color="text.secondary">Preencha os campos e veja o preview ao lado</Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(249,115,22,0.15)' }} />

        {isReceita
          ? <ReceitaForm form={receitaForm} setForm={setReceitaForm} fieldSx={fieldSx} />
          : <AtestadoForm form={atestadoForm} setForm={setAtestadoForm} fieldSx={fieldSx} />
        }

        <Divider sx={{ borderColor: 'rgba(249,115,22,0.1)', mt: 'auto' }} />

        <Box display="flex" gap={1.5} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ flex: 1, bgcolor: ORANGE, '&:hover': { bgcolor: '#EA580C' }, borderRadius: 2, fontWeight: 700, boxShadow: '0 2px 10px rgba(249,115,22,0.3)' }}
          >
            Imprimir
          </Button>
          <Button
            variant="outlined"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsApp}
            disabled={enviandoWhatsApp}
            sx={{ flex: 1, borderColor: '#25d366', color: '#25d366', borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: 'rgba(37,211,102,0.06)', borderColor: '#25d366' } }}
          >
            {enviandoWhatsApp ? 'Gerando PDF...' : 'WhatsApp'}
          </Button>
        </Box>
        {erroWhatsApp && <Typography variant="caption" color="error">{erroWhatsApp}</Typography>}
        {!paciente?.telefone && !paciente?.whatsapp && (
          <Typography variant="caption" color="text.disabled">
            Paciente sem telefone cadastrado — o PDF será baixado, mas o WhatsApp abrirá sem número.
          </Typography>
        )}

        <Divider sx={{ borderColor: 'rgba(249,115,22,0.1)' }} />

        {!documentoAssinatura ? (
          <Button
            variant="outlined"
            startIcon={<VerifiedIcon />}
            onClick={handleAssinarDigital}
            disabled={assinandoDoc}
            sx={{ borderColor: '#2563eb', color: '#2563eb', borderRadius: 2, fontWeight: 700, '&:hover': { bgcolor: 'rgba(37,99,235,0.06)', borderColor: '#2563eb' } }}
          >
            {assinandoDoc ? 'Enviando...' : 'Assinar digitalmente (ICP-Brasil)'}
          </Button>
        ) : documentoAssinatura.status === 'assinado' ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <CheckCircleIcon sx={{ color: '#16a34a', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={700} color="#16a34a">Documento assinado digitalmente!</Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => window.open(documentoAssinatura.downloadUrl, '_blank')}
              disabled={!documentoAssinatura.downloadUrl}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' }, borderRadius: 2, fontWeight: 700 }}
            >
              Baixar PDF assinado
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Aguardando o dentista assinar com o certificado digital na aba que abrimos.
            </Typography>
            <Box display="flex" gap={1.5}>
              <Button size="small" onClick={() => window.open(documentoAssinatura.link, '_blank')} sx={{ color: '#2563eb', fontWeight: 700 }}>
                Reabrir link de assinatura
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleVerificarAssinatura}
                disabled={verificandoAssinatura}
                sx={{ borderColor: '#2563eb', color: '#2563eb', fontWeight: 700 }}
              >
                {verificandoAssinatura ? 'Verificando...' : 'Verificar assinatura'}
              </Button>
            </Box>
          </Box>
        )}
        {erroAssinatura && <Typography variant="caption" color="error">{erroAssinatura}</Typography>}
      </Paper>

      {/* Painel direito — preview */}
      <Box sx={{ flex: '1 1 320px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Chip label="Preview do documento" size="small" sx={{ bgcolor: 'rgba(249,115,22,0.1)', color: ORANGE, fontWeight: 700, fontSize: '0.72rem' }} />
          <Typography variant="caption" color="text.disabled">Exatamente como será impresso</Typography>
        </Box>

        {isReceita
          ? <ReceitaPreview form={receitaForm} user={user} logo={logo} assinatura={assinatura} clinica={clinica} />
          : <AtestadoPreview form={atestadoForm} user={user} logo={logo} assinatura={assinatura} clinica={clinica} />
        }
      </Box>
    </Box>
  );
};

export default EmitirDocumento;
